import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Copy, ExternalLink, X } from 'lucide-react';
import { creativeWorkbenchApi } from '@/services/api';
import { BriefCreatorOverlay } from '@/components/brief/BriefCreatorOverlay';
import { LineageBreadcrumb } from './LineageBreadcrumb';

interface Props {
  assetId: string;
  clientUid: string;
  onClose: () => void;
}

interface LineagePayload {
  asset?: Record<string, any>;
  creative?: Record<string, any> | null;
  ad?: Record<string, any> | null;
  brief?: Record<string, any> | null;
  strategic_playbook?: Record<string, any> | null;
  lineage_state?: string;
}

export function AssetDetailDrawer({ assetId, clientUid, onClose }: Props) {
  const navigate = useNavigate();
  const [data, setData] = useState<LineagePayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [backfillOpen, setBackfillOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    creativeWorkbenchApi
      .getAssetLineage(assetId, clientUid)
      .then((r) => {
        if (!cancelled) setData(r as LineagePayload);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message ?? 'Failed to load lineage');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [assetId, clientUid]);

  const asset = data?.asset;

  return (
    <div
      className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40"
      onClick={onClose}
    >
      <aside
        className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <div>
            <h3 className="text-sm font-bold text-navy-900">
              {(asset?.asset_type as string) || 'Asset'}
            </h3>
            <div className="text-[10px] font-mono text-slate-400">{assetId}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X size={16} />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto">
          {loading && <div className="p-4 text-xs text-slate-400">Loading lineage...</div>}
          {error && <div className="p-4 text-xs text-rose-600">{error}</div>}
          {data && (
            <>
              <div className="aspect-video bg-slate-100 flex items-center justify-center">
                {typeof asset?.preview_url === 'string' && asset.preview_url.startsWith('http') ? (
                  // eslint-disable-next-line jsx-a11y/alt-text
                  <img src={asset.preview_url} className="max-h-full max-w-full object-contain" />
                ) : (
                  <span className="text-xs font-mono text-slate-400 text-center px-4">
                    {(asset?.preview_url as string) || 'No preview available'}
                  </span>
                )}
              </div>
              <div className="p-4 space-y-4">
                <LineageBreadcrumb
                  playbook={data.strategic_playbook}
                  brief={data.brief}
                  creative={data.creative}
                  ad={data.ad}
                  asset={asset}
                  state={data.lineage_state ?? 'linked'}
                />
                <section className="space-y-1">
                  <SectionTitle>Metadata</SectionTitle>
                  <MetaRow label="Asset type" value={asset?.asset_type} />
                  <MetaRow label="Channel" value={asset?.channel ?? 'unknown'} />
                  <MetaRow label="Scene tags" value={(asset?.scene_tags ?? []).join(', ') || '—'} />
                  <MetaRow
                    label="Quality"
                    value={`${Math.round(((asset?.quality_score as number) ?? 0) * 100)}%`}
                  />
                  <MetaRow
                    label="Brand compliance"
                    value={`${Math.round(((asset?.brand_compliance as number) ?? 0) * 100)}%`}
                  />
                </section>
                <section className="space-y-1">
                  <SectionTitle>Actions</SectionTitle>
                  {(data?.ad?.ad_id || asset?.ad_id) && (
                    <button
                      onClick={() => setBackfillOpen(true)}
                      className="w-full inline-flex items-center gap-2 text-xs text-sky-600 hover:text-sky-800 py-1.5"
                      title="Reverse-engineer a creative brief from this ad"
                    >
                      <ArrowLeft size={12} /> Back-fill brief from this ad
                    </button>
                  )}
                  <button
                    onClick={() =>
                      navigator.clipboard.writeText(asset?.preview_url ?? asset?.asset_id)
                    }
                    className="w-full inline-flex items-center gap-2 text-xs text-slate-600 hover:text-teal-600 py-1.5"
                  >
                    <Copy size={12} /> Copy GCS URI
                  </button>
                  {asset?.preview_url && typeof asset.preview_url === 'string' && asset.preview_url.startsWith('http') && (
                    <a
                      href={asset.preview_url}
                      target="_blank"
                      rel="noreferrer"
                      className="w-full inline-flex items-center gap-2 text-xs text-slate-600 hover:text-teal-600 py-1.5"
                    >
                      <ExternalLink size={12} /> Open in new tab
                    </a>
                  )}
                </section>
              </div>
            </>
          )}
        </div>
      </aside>

      {/* Phase 1 — back-fill brief from the linked ad */}
      <BriefCreatorOverlay
        open={backfillOpen}
        onClose={() => setBackfillOpen(false)}
        onBriefCreated={(briefId) => {
          setBackfillOpen(false);
          navigate(`/creative/briefs/${briefId}?lens=strategy`);
        }}
        defaultClientUid={clientUid}
        defaultIntent="from_ad"
        defaultAdId={
          (data?.ad?.ad_id as string) ||
          (asset?.ad_id as string) ||
          undefined
        }
      />
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h4 className="text-[10px] font-mono uppercase text-slate-500">{children}</h4>;
}

function MetaRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-slate-500">{label}</span>
      <span className="font-mono text-navy-900 truncate max-w-[200px]">
        {String(value ?? '—')}
      </span>
    </div>
  );
}
